import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  Alert, InputAdornment, IconButton, CircularProgress,
  Stepper, Step, StepLabel,
} from '@mui/material';
import {
  Email, Key, Lock, Visibility, VisibilityOff,
  AutoAwesome, ArrowForward, ArrowBack, CheckCircle
} from '@mui/icons-material';
import { authAPI } from '../services/api';

const STEPS = ['Enter Email', 'Verify OTP', 'New Password'];

export default function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [step, setStep]         = useState(0);
  const [email, setEmail]       = useState('');
  const [otp, setOtp]           = useState('');
  const [newPw, setNewPw]       = useState('');
  const [confirmPw, setConfirm] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [resendTimer, setTimer] = useState(0);
  const [done, setDone]         = useState(false);

  const startResendTimer = () => {
    setTimer(60);
    const interval = setInterval(() => {
      setTimer(t => { if (t <= 1) { clearInterval(interval); return 0; } return t - 1; });
    }, 1000);
  };

  // Step 1 — Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await authAPI.sendForgotOtp({ email });
      setSuccess(`OTP sent to ${email}`);
      setStep(1);
      startResendTimer();
    } catch (err) {
      setError(err.response?.data?.message || 'No account found with this email');
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { setError('Enter 6-digit OTP'); return; }
    setError(''); setLoading(true);
    try {
      await authAPI.verifyForgotOtp({ email, otp });
      setSuccess('OTP verified! Set your new password.');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired OTP');
    } finally {
      setLoading(false);
    }
  };

  // Step 3 — Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { setError('Passwords do not match'); return; }
    if (newPw.length < 6) { setError('Password must be at least 6 characters'); return; }
    setError(''); setLoading(true);
    try {
      await authAPI.resetPassword({ email, newPassword: newPw });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    try {
      await authAPI.sendForgotOtp({ email });
      setSuccess('New OTP sent!');
      startResendTimer();
    } catch { setError('Failed to resend OTP'); }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(ellipse at 50% 30%, rgba(103,58,183,0.1) 0%, transparent 60%), #000000',
    }}>
      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 460, px: 2 }}>
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{ display: 'inline-flex', p: 2, borderRadius: '50%',
            bgcolor: 'rgba(103,58,183,0.15)', border: '1px solid rgba(179,136,255,0.3)', mb: 2 }}>
            <AutoAwesome sx={{ fontSize: 36, color: '#b39ddb' }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#b39ddb', mb: 0.5 }}>
            Reset Password
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            We'll send an OTP to verify your identity
          </Typography>
        </Box>

        <Card sx={{ background: 'rgba(10,22,40,0.85)', backdropFilter: 'blur(20px)',
          border: '1px solid rgba(179,136,255,0.15)' }}>
          <CardContent sx={{ p: 4 }}>

            {!done ? (
              <>
                {/* Stepper */}
                <Stepper activeStep={step} sx={{ mb: 3 }}>
                  {STEPS.map(label => (
                    <Step key={label}>
                      <StepLabel sx={{
                        '& .MuiStepLabel-label': { color: 'text.secondary', fontSize: 11 },
                        '& .MuiStepLabel-label.Mui-active': { color: '#b39ddb' },
                        '& .MuiStepIcon-root.Mui-active': { color: '#7c4dff' },
                        '& .MuiStepIcon-root.Mui-completed': { color: '#66bb6a' },
                      }}>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

                {/* Step 1 — Email */}
                {step === 0 && (
                  <form onSubmit={handleSendOtp}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      Enter your registered email address and we'll send you an OTP to reset your password.
                    </Typography>
                    <TextField fullWidth label="Email Address" type="email"
                      value={email} onChange={e => setEmail(e.target.value)}
                      required autoFocus sx={{ mb: 3 }}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ color: '#b39ddb', fontSize: 20 }} /></InputAdornment> }}
                    />
                    <Button type="submit" fullWidth variant="contained" size="large"
                      disabled={loading} endIcon={<ArrowForward />}
                      sx={{ py: 1.5, fontSize: 15, mb: 2,
                        background: 'linear-gradient(135deg, #7c4dff, #b39ddb)',
                        '&:hover': { background: 'linear-gradient(135deg, #651fff, #7c4dff)' } }}>
                      {loading ? <CircularProgress size={22} color="inherit" /> : 'Send OTP'}
                    </Button>
                  </form>
                )}

                {/* Step 2 — OTP */}
                {step === 1 && (
                  <form onSubmit={handleVerifyOtp}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Box sx={{ fontSize: 48, mb: 1 }}>📧</Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        OTP sent to <strong style={{ color: '#b39ddb' }}>{email}</strong>
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth label="Enter 6-Digit OTP" value={otp}
                      onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required autoFocus sx={{ mb: 2 }}
                      inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: 28, letterSpacing: 12, fontWeight: 800 } }}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Key sx={{ color: '#b39ddb' }} /></InputAdornment> }}
                    />
                    <Button type="submit" fullWidth variant="contained" size="large"
                      disabled={loading || otp.length !== 6}
                      sx={{ py: 1.5, fontSize: 15, mb: 2,
                        background: 'linear-gradient(135deg, #7c4dff, #b39ddb)',
                        '&:hover': { background: 'linear-gradient(135deg, #651fff, #7c4dff)' } }}>
                      {loading ? <CircularProgress size={22} color="inherit" /> : 'Verify OTP'}
                    </Button>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Button startIcon={<ArrowBack />}
                        onClick={() => { setStep(0); setOtp(''); setError(''); setSuccess(''); }}
                        sx={{ color: 'text.secondary', textTransform: 'none' }}>
                        Back
                      </Button>
                      <Button onClick={handleResend} disabled={resendTimer > 0}
                        sx={{ color: resendTimer > 0 ? 'text.secondary' : '#b39ddb', textTransform: 'none' }}>
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                      </Button>
                    </Box>
                  </form>
                )}

                {/* Step 3 — New Password */}
                {step === 2 && (
                  <form onSubmit={handleResetPassword}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      Create a strong new password for your account.
                    </Typography>
                    <TextField fullWidth label="New Password"
                      type={showPw ? 'text' : 'password'} value={newPw}
                      onChange={e => setNewPw(e.target.value)} required autoFocus sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><Lock sx={{ color: '#b39ddb', fontSize: 20 }} /></InputAdornment>,
                        endAdornment: <InputAdornment position="end">
                          <IconButton onClick={() => setShowPw(v => !v)} size="small">
                            {showPw ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>,
                      }}
                    />
                    <TextField fullWidth label="Confirm New Password"
                      type={showPw ? 'text' : 'password'} value={confirmPw}
                      onChange={e => setConfirm(e.target.value)} required sx={{ mb: 3 }}
                      error={confirmPw !== '' && newPw !== confirmPw}
                      helperText={confirmPw !== '' && newPw !== confirmPw ? 'Passwords do not match' : ''}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Lock sx={{ color: '#b39ddb', fontSize: 20 }} /></InputAdornment> }}
                    />
                    <Button type="submit" fullWidth variant="contained" size="large"
                      disabled={loading}
                      sx={{ py: 1.5, fontSize: 15,
                        background: 'linear-gradient(135deg, #7c4dff, #b39ddb)',
                        '&:hover': { background: 'linear-gradient(135deg, #651fff, #7c4dff)' } }}>
                      {loading ? <CircularProgress size={22} color="inherit" /> : 'Reset Password'}
                    </Button>
                  </form>
                )}
              </>
            ) : (
              /* Success State */
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircle sx={{ fontSize: 72, color: '#66bb6a', mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#66bb6a', mb: 1 }}>
                  Password Reset!
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                  Your password has been successfully reset. You can now login with your new password.
                </Typography>
                <Button fullWidth variant="contained" size="large"
                  onClick={() => navigate('/login')}
                  sx={{ py: 1.5, background: 'linear-gradient(135deg, #0288d1, #ffffff)' }}>
                  Go to Login
                </Button>
              </Box>
            )}

            {!done && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <span
  onClick={() => navigate('/login')}
  style={{ color: '#b39ddb', fontSize: 13, textDecoration: 'none', cursor: 'pointer' }}
    >
  ← Back to Login
  </span>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}